import React from 'react';
import { Shield } from 'lucide-react';

interface PolicySection {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
  groups?: { heading: string; bullets: string[] }[];
  closing?: string;
}

const LAST_UPDATED = '12-08-2026';

const sections: PolicySection[] = [
  {
    title: '1. Zero Data Processing by the Developer',
    paragraphs: [
      'Gaadimatic Technologies Private Limited does not collect, own, retain, or independently process any data transmitted through the Applications. We do not operate or host any backend server or database that stores this data.',
    ],
    bullets: [
      'All data entry, transmission, and calculations are handled directly by the proprietary ERP application infrastructure deployed and managed by Phoenitech Travels Private Limited.',
      "The connection is strictly between the mobile application interface on the employee's device and the Company's ERP system.",
      'Any records (including the recent-activity lists described in Section 3) are fetched and displayed to the logged-in employee for their own reference. The Applications do not permanently store this data outside of the Company\u2019s ERP; nothing is copied to, or retained by, Developer-owned infrastructure.',
    ],
  },
  {
    title: '2. Access and Login',
    bullets: [
      "Employees can only log in to the Applications if they have already been onboarded by the Company's administration team through the Company's web-based ERP system.",
      "Login is performed using the employee's registered mobile number, verified against the record created during web onboarding, followed by One-Time Password (OTP) verification.",
      "At present, login uses a fixed test OTP for verification purposes. Dynamic OTPs delivered via SMS to the employee's registered mobile number will be enabled in a future release, at which point the SMS permission described in Section 4 will be used to auto-read the OTP.",
      'Employees who have not been onboarded in the web ERP cannot register, log in, or access the Applications by any means.',
    ],
  },
  {
    title: "3. Information Handled by the Company's ERP",
    paragraphs: [
      "The Applications function purely as a digital data-entry and data-viewing interface for Phoenitech Travels Private Limited. The following operational data is exchanged directly with the Company's ERP system:",
    ],
    groups: [
      {
        heading: 'Profile Screen (both Applications)',
        bullets: [
          'Displays basic profile details (e.g., name, employee/vehicle assignment details) that were entered during onboarding in the web ERP.',
          'Profile photographs and identity/verification documents collected during web onboarding are not displayed, downloaded, or accessible within the mobile Applications.',
        ],
      },
      {
        heading: 'Driver Mobile Application',
        bullets: [
          'Trip start and trip completion, recorded with odometer readings and a photo of the odometer/relevant proof.',
          'Check-in/check-out timestamps and vehicle number.',
          'A history view of the most recent trips (details such as timestamps, odometer readings, and status) \u2014 this history view does not display images.',
        ],
      },
      {
        heading: 'Maintenance Mobile Application',
        bullets: [
          'Vehicle inspection data, workshop notes, and internal parts/job-card logs.',
          'Fuel entries and petty cash entries.',
          'A history view of the most recent 20 records, shown with both entry details and associated uploaded images (e.g., bill/receipt photos).',
        ],
      },
    ],
  },
  {
    title: '4. Device Permissions & Media Transmission',
    paragraphs: [
      'The Applications request only the following device permissions, strictly limited to the purposes below:',
    ],
    bullets: [
      'Camera: To capture real-time photos of fuel/bill receipts, odometer readings, and vehicle condition/maintenance proofs at the time of entry.',
      'Storage/Media access: To select or temporarily access images being uploaded as part of a fuel entry, odometer reading, or maintenance record.',
      'Notifications: To alert the employee about operational updates within the Applications (e.g., trip status, entry confirmations, or reminders relevant to their role).',
      "SMS: To automatically read and verify the One-Time Password (OTP) sent to the employee's registered mobile number during login, so the employee does not need to manually type it in.",
    ],
    closing:
      "No other device permissions (contacts, microphone, call logs, etc.) are requested. All captured or selected images are transmitted directly to the Company's ERP system; the Developer does not retain a separate copy. Notification content is limited to in-app operational alerts and does not involve marketing or promotional messaging.",
  },
  {
    title: '5. Location Data Disclaimer',
    paragraphs: [
      'The Driver Mobile Application and Maintenance Mobile Application do not track, monitor, or capture GPS/geographical location coordinates from the mobile device at any time. Any vehicle tracking, routing, or fleet telematics are managed independently by Phoenitech Travels Private Limited through separate vehicle-mounted GPS hardware or native ERP systems. The Developer has no access to any location data.',
    ],
  },
  {
    title: '6. Third-Party Libraries and Advertisements',
    paragraphs: [
      "As an exclusive business-to-business (B2B) enterprise tool, the Applications contain no third-party advertising tools, marketing SDKs, or external analytics packages. Google Play Services are used solely to deliver secure app binaries and updates to the Company's registered devices.",
    ],
  },
  {
    title: '7. Employee Accounts and Data Deletion',
    bullets: [
      'All driver and mechanic user profiles are created, managed, and deleted directly within the web ERP system by the administration team at Phoenitech Travels Private Limited.',
      'The mobile Applications do not provide a self-service option to delete an account or profile from within the app.',
      'Because Gaadimatic Technologies Private Limited has no administrative access to the ERP system or user databases, any requests regarding data access, correction, or account/profile deletion must be routed directly to the management of Phoenitech Travels Private Limited.',
    ],
  },
  {
    title: '8. Changes to this Policy',
    paragraphs: [
      'We may update this privacy statement if application permissions or functionality change to accommodate new operational features requested by the Company. The "Last updated" date at the top will reflect the most recent revision.',
    ],
  },
];

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <section className="relative pt-32 pb-12 lg:pt-40 lg:pb-16 bg-gray-50">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-accent-orange/5" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full mb-6">
              <Shield className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-primary-700">Your privacy matters</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-navy-950 leading-tight mb-4">
              Privacy Policy
            </h1>
            <p className="text-gray-500">Last updated: {LAST_UPDATED}</p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <p className="text-gray-600 leading-relaxed mb-4">
              Gaadimatic Technologies Private Limited, Hyderabad ("Developer," "we," "our," or "us")
              is the software development entity that built the Driver Mobile Application and
              Maintenance Mobile Application (collectively, the "Applications").
            </p>
            <p className="text-gray-600 leading-relaxed">
              These Applications are custom, closed-loop corporate utilities designed exclusively for
              the internal fleet management operations of Phoenitech Travels Private Limited ("Client
              Company") and are restricted solely to authorized employees of the Client Company. These
              Applications are not intended for public use, and no member of the public may register
              or log in.
            </p>

            {sections.map((section) => (
              <div key={section.title} className="mt-12">
                <h2 className="text-2xl font-bold text-navy-950 mb-4">{section.title}</h2>
                {section.paragraphs?.map((text) => (
                  <p key={text} className="text-gray-600 leading-relaxed mb-4">
                    {text}
                  </p>
                ))}
                {section.bullets && (
                  <ul className="list-disc pl-6 space-y-2 text-gray-600 leading-relaxed">
                    {section.bullets.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
                {section.groups?.map((group) => (
                  <div key={group.heading} className="mt-6">
                    <h3 className="font-semibold text-navy-950 mb-3">{group.heading}</h3>
                    <ul className="list-disc pl-6 space-y-2 text-gray-600 leading-relaxed">
                      {group.bullets.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
                {section.closing && (
                  <p className="text-gray-600 leading-relaxed mt-4">{section.closing}</p>
                )}
              </div>
            ))}

            <p className="mt-12 p-5 bg-gray-50 border border-gray-200 rounded-xl text-gray-600 text-sm">
              NOTE: These mentioned applications have no commercial element, nor is any revenue
              generated out of them.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
